import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  Checkbox,
  CheckboxChangeEvent,
  Input,
  InputChangeEvent,
} from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import useLocale from "../../../../../hooks/useLocale";
import deviceService from "../../../../../services/device.service";
import { DeviceAllocation } from "../../../../../types/device/deviceAllocation";
import { Dictionary } from "../../../../../types/Dictionary";
import { PlatformUserDetail, TinyUser, User } from "../../../../../types/user";
import useSwal from "../../../../../hooks/useSwal";

interface AddDeviceDialogProps {
  toggleDialog: () => void;
  userDevices: PlatformUserDetail[];
  upsertDevice: (devices: PlatformUserDetail[]) => void;
  user: User;
}

interface PlatformUserDetailWithCheckbox {
  allocation: DeviceAllocation;
  isSelected: boolean;
}

const AddDeviceDialog: React.FC<AddDeviceDialogProps> = ({
  toggleDialog,
  userDevices,
  upsertDevice,
  user,
}) => {
  const userTinyUserObj: TinyUser = {
    id: user.id,
    loginUserId: user.loginUserId,
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    email: user.email,
    isArchived: user.isArchived,
  };
  const Swal = useSwal();
  const localeCtx = useLocale();
  const [devices, setDevices] = useState<PlatformUserDetailWithCheckbox[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceAllocation[]>(
    userDevices.map((item) => {
      let deviceAlloc: DeviceAllocation = {
        device: item,
        user: userTinyUserObj
      };
      return deviceAlloc;
    })
  );
  const [searchText, setSearchText] = useState<string>("");
  const [disableAttach, setDisableAttach] = useState<boolean>(false);
  const [translationsLoading, setTranslationsLoading] =
    useState<boolean>(false);
  const [translations, setTranslations] = useState<
    Dictionary<string> | undefined
  >(
    localeCtx?.selectedLocale?.current.componentTranslations["AddDeviceDialog"]
  );

  useEffect(() => {
    setDevicesState();
  }, []);

  useEffect(() => {
    setDisableAttach(
      devices.filter(
        (d) => d.allocation.user?.id !== user.id && d.isSelected === true
      ).length === 0
    );
  }, [devices]);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "AddDeviceDialog"
      ]
    ) {
      fetchTranslations();
    }
  }, [localeCtx?.selectedLocale]);

  const fetchTranslations = async () => {
    try {
      setTranslationsLoading(true);
      const resp = await localeCtx?.setComponentTranslations("AddDeviceDialog");
      setTranslations(resp);
    } catch (err) {
      console.error(err);
      setTranslations(
        localeCtx?.selectedLocale?.previous.componentTranslations[
          "AddDeviceDialog"
        ]
      );
      localeCtx?.setPreviousAppLocale("AddDeviceDialog");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Couldn't Switch Language",
      });
    } finally {
      setTimeout(() => {
        setTranslationsLoading(false);
      }, 100);
    }
  };

  const fetchLabelKeyTranslation = (
    key: string,
    defaultValue: string
  ): string => {
    return translations && translations[key] ? translations[key] : defaultValue;
  };

  const updateSearchText = (e: InputChangeEvent) => {
    setSearchText(e.value);
  };

  const setDevicesState = async () => {
    const allDevice = await deviceService.getAllCustomerDevice();
    const allDevicewitCheckedValue = allDevice.map((dev) => {
      const deviceSet: PlatformUserDetailWithCheckbox = {
        allocation: dev,
        isSelected: false,
      };
      selectedDevice.map((item) => {
        if (item.device.deviceIdentifier === dev.device.deviceIdentifier) {
          deviceSet.isSelected = true;
          deviceSet.allocation = item;
        }
      });
      return deviceSet;
    });
    setDevices(allDevicewitCheckedValue);
  };

  const attachDevice = () => {
    upsertDevice(
      devices
        .filter((dev) => dev.isSelected)
        .map((item) => {
          return item.allocation.device;
        })
    );
    toggleDialog();
  };

  const upsertSelectedDevice = (
    changedevice: PlatformUserDetailWithCheckbox,
    e: CheckboxChangeEvent
  ) => {
    setDevices(
      devices.map((item) => {
        if (
          item.allocation.device.deviceIdentifier ===
          changedevice.allocation.device.deviceIdentifier
        ) {
          item.isSelected = e.value;
        }
        return item;
      })
    );
  };

  const searchDevice = (device: PlatformUserDetailWithCheckbox) => {
    const text = searchText.trim().toLocaleLowerCase();
    if (device.allocation.device.platform.toLocaleLowerCase().includes(text)
      || device.allocation.device.deviceIdentifier.toLocaleLowerCase().includes(text)
    ){
      return true;
    }
    return false;
  };

  return (
    <Dialog
      title={`${
        translationsLoading
          ? "Attach Device"
          : fetchLabelKeyTranslation("AttachDeviceTitle", "Attach Device")
      }`}
      onClose={toggleDialog}
    >
      <div
        className="attachDevieBox"
        style={{
          minWidth: "400px",
          maxWidth: "400px",
          minHeight: "200px",
        }}
      >
        <div className="detailCol">
          <div className="detailColBox">
            <div className="formBox">
              <div className="formBoxRow p-t-5 p-b-15">
                <div className="formBoxAction">
                  <div className="detailColBox p-r-10">
                    <div className="UserLiSearch p-b-5">
                      <div className="formInput">
                        <Input
                          style={{ height: "32px" }}
                          placeholder={`${
                            translationsLoading
                              ? "Search…"
                              : fetchLabelKeyTranslation(
                                  "SearchbBoxPlaceHolder",
                                  "Search…"
                                )
                          }`}
                          onChange={updateSearchText}
                        />
                      </div>
                    </div>
                    <div className="detailColBox">
                      <div className="dashedBox p-10 m-b-10 float-left w-100 radius-6 border-black-3 border-w-2 border-dashed">
                        <div
                          className="scroller"
                          style={{
                            maxHeight: "285px",
                            minHeight: "285px",
                            overflowY: "auto",
                            overflowX: "hidden",
                            paddingRight: "20px",
                          }}
                        >
                          {devices
                            .filter((item) => searchDevice(item))
                            .map((item) => {
                              return (
                                <div
                                  key={item.allocation.device.deviceIdentifier}
                                  className="UserRow w-100 float-left p-t-4 p-b-4 border-bottom-solid border-w-1 border-black-1"
                                >
                                  <div className="deviceListing">
                                    <div className="usrTableRow d-flex align-items-center justify-content-between">
                                      <div className="userTblCol">
                                        <div className="tblUsr">
                                          <div className="topUsrAreaPic">
                                            <div className="topUsrAreaPic-i">
                                              <div className="contact-list-icon bg-darksteelblue">
                                                <div className="contact-list-icon-txt">
                                                  <i className="bi bi-bag"></i>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="topUsrAreaPic-tx hoverTextFull">
                                              <span className="text-muted fs-14 line-height-2 mx-td-spn viewFullWordHover">
                                                {
                                                  item.allocation.device
                                                    .deviceIdentifier
                                                }
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="userTblCol">
                                        <Checkbox
                                          checked={item.isSelected}
                                          onChange={(e) =>
                                            upsertSelectedDevice(item, e)
                                          }
                                          disabled={
                                            item.allocation.user?.id === user.id ||
                                            devices.filter((dev)=>dev.isSelected===true).find((d)=>d.allocation.device.id!==item.allocation.device.id && d.allocation.device.platform===item.allocation.device.platform)!=undefined
                                          }
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DialogActionsBar>
        <button
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
          onClick={toggleDialog}
        >
          {`${
            translationsLoading
              ? "Cancel"
              : fetchLabelKeyTranslation("CancelButton", "Cancel")
          }`}
        </button>
        <button
          className={`k-button k-button-md k-rounded-md bg-primary btn text-white
            ${disableAttach ? "disabledBtn" : ""}`}
          onClick={attachDevice}
          disabled={disableAttach}
        >
          {`${
            translationsLoading
              ? "Save"
              : fetchLabelKeyTranslation("SaveButton", "Save")
          }`}
        </button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default AddDeviceDialog;
